import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useTheme } from "../../contexts/ThemeContext";
import { useLocalization } from "../../contexts/LocalizationContext";
import Button from "../../components/UI/Button";
import Input from "../../components/UI/Input";
import logo from "../../assets/logoProd.png";
import logoLight from "../../assets/logoProdLight.png";
import { useIsMobile } from "../../App";

const ResetPasswordScreen = () => {
  const [formData, setFormData] = useState({
    code: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [email, setEmail] = useState("");

  const { resetPassword } = useAuth();
  const { theme } = useTheme();
  const { t } = useLocalization();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();

  useEffect(() => {
    // Отримуємо email з location state або localStorage
    const emailFromState = location.state?.email;
    const emailFromStorage = localStorage.getItem("resetPasswordEmail");
    
    if (emailFromState) {
      setEmail(emailFromState);
      localStorage.setItem("resetPasswordEmail", emailFromState);
    } else if (emailFromStorage) {
      setEmail(emailFromStorage);
    } else {
      // Якщо немає email, перенаправляємо на сторінку забутого пароля
      navigate("/forgot-password");
    }
  }, [location.state, navigate]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Очищаємо помилку для цього поля
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.code.trim()) {
      newErrors.code = t("verificationCodeRequired") || "Код підтвердження обов'язковий";
    } else if (formData.code.trim().length !== 6) {
      newErrors.code = t("verificationCodeLength") || "Код повинен містити 6 цифр";
    }

    if (!formData.newPassword.trim()) {
      newErrors.newPassword = t("passwordRequired") || "Введіть пароль";
    } else if (formData.newPassword.length < 8) {
      newErrors.newPassword = t("passwordMinLength") || "Пароль повинен бути не менше 8 символів";
    }

    if (!formData.confirmPassword.trim()) {
      newErrors.confirmPassword = t("confirmPasswordRequired") || "Підтвердіть пароль";
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = t("passwordsDontMatch") || "Паролі не співпадають";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleResetPassword = async () => {
    if (!validateForm()) return;

    setLoading(true);
    setErrors({});

    try {
      const result = await resetPassword(
        email,
        formData.code.trim(),
        formData.newPassword,
        formData.confirmPassword
      );
      
      if (result.success) {
        // Видаляємо email з localStorage
        localStorage.removeItem("resetPasswordEmail");
        // Перенаправляємо на сторінку логіну з повідомленням про успіх
        navigate("/login", { 
          state: { message: t("passwordResetSuccess") || "Пароль успішно скинуто" } 
        });
      } else {
        setErrors({ 
          general: result.error || t("resetPasswordFailed") || "Помилка скидання пароля" 
        });
      }
    } catch (error) {
      setErrors({ 
        general: t("resetPasswordFailed") || "Помилка скидання пароля" 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleResetPassword();
    }
  };

  const handleBackToForgotPassword = () => {
    navigate("/forgot-password");
  };

  const containerStyle = {
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    background: theme.background,
    padding: isMobile ? "8px 0" : "32px 0",
    boxSizing: "border-box",
  };

  const formStyle = {
    width: isMobile ? "96vw" : 400,
    maxWidth: 400,
    background: theme.surface,
    borderRadius: isMobile ? 8 : 16,
    boxShadow: theme.isDark ? "0 2px 8px #0005" : "0 2px 8px #0002",
    padding: isMobile ? "16px 8px" : "32px",
    boxSizing: "border-box",
  };

  const titleStyles = {
    fontSize: "26px",
    fontWeight: "bold",
    textAlign: "center",
    marginBottom: "16px",
    color: theme.text,
  };

  const subtitleStyles = {
    fontSize: "16px",
    textAlign: "center",
    marginBottom: "32px",
    color: theme.textSecondary || theme.text,
    opacity: 0.8,
  };

  const formStyles = {
    display: "flex",
    flexDirection: "column",
    gap: "20px",
    marginBottom: "24px",
    alignItems: "center",
  };

  const linkStyles = {
    textAlign: "center",
    color: theme.primary,
    cursor: "pointer",
    textDecoration: "none",
    fontSize: "16px",
    width: "100%",
  };

  const errorStyles = {
    color: theme.error,
    fontSize: "14px",
    textAlign: "center",
    marginBottom: "16px",
  };

  const logoStyles = {
    display: "block",
    margin: "0 auto 24px auto",
    width: "250px",
    height: "auto",
  };

  const isDark = theme.isDark;
  const logoToShow = isDark ? logoLight : logo;

  const isFormValid =
    formData.code &&
    formData.newPassword &&
    formData.confirmPassword &&
    formData.code.trim().length === 6 &&
    formData.newPassword.length >= 8 &&
    formData.newPassword === formData.confirmPassword;

  return (
    <div style={containerStyle}>
      <div style={formStyle}>
        <img src={logoToShow} alt="Logo" style={logoStyles} />
        <h1 style={titleStyles}>{t("resetPassword") || "Скинути пароль"}</h1>
        
        <p style={subtitleStyles}>
          {t("resetPasswordDesc") || "Введіть код з пошти та новий пароль для"}
          <strong> {email}</strong>
        </p>

        {errors.general && <div style={errorStyles}>{errors.general}</div>}

        <div style={formStyles}>
          <Input
            label={t("verificationCode") || "Код підтвердження"}
            type="text"
            placeholder="123456"
            value={formData.code}
            onChange={(e) => handleInputChange("code", e.target.value)}
            onKeyPress={handleKeyPress}
            error={errors.code}
            required
            autoComplete="off"
            maxLength="6"
          />

          <Input
            label={t("newPassword") || "Новий пароль"}
            type="password"
            placeholder={t("newPassword") || "Новий пароль"}
            value={formData.newPassword}
            onChange={(e) => handleInputChange("newPassword", e.target.value)}
            onKeyPress={handleKeyPress}
            error={errors.newPassword}
            required
            autoComplete="new-password"
          />

          <Input
            label={t("confirmPassword") || "Підтвердити пароль"}
            type="password"
            placeholder={t("confirmPassword") || "Підтвердити пароль"}
            value={formData.confirmPassword}
            onChange={(e) => handleInputChange("confirmPassword", e.target.value)}
            onKeyPress={handleKeyPress}
            error={errors.confirmPassword}
            required
            autoComplete="new-password"
          />

          <Button
            className="reset-password-button"
            onClick={handleResetPassword}
            loading={loading}
            disabled={!isFormValid}
            size={isMobile ? "small" : "medium"}
            style={{ 
              whiteSpace: "nowrap",
              width: "auto",
              minWidth: "200px",
              padding: "12px 32px"
            }}
          >
            {t("resetPassword") || "Скинути пароль"}
          </Button>
        </div>

        <div style={linkStyles} onClick={handleBackToForgotPassword}>
          ← {t("backToForgotPassword") || "Повернутися до забутого пароля"}
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordScreen;
